import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, red } from '@mui/material/colors';
import {
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';

import Table from '~/components/v2/common/Table/Table';

import * as AccommodationService from '~/services/AccommodationService';

interface ManageCatalogGroupsModalProps {
  open: boolean;
  onClose: () => void;
}

interface CatalogGroupRowData extends AccommodationService.AdminCatalogGroup {
  isNew?: boolean;
}

interface PendingChanges {
  name?: string;
  description?: string | null;
  displayOrder?: number;
  icon?: string | null;
  // For new rows, we also need code and type
  code?: string;
  type?: string;
}

const NEW_ROW_ID_PREFIX = 'new-';

function EditableTextField(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    apiRef.current.setEditCellValue({ id, field, value: event.target.value });
  };

  return <TextField size="small" value={value ?? ''} onChange={handleChange} fullWidth variant="outlined" />;
}

function NumberEditCell(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = event.target.value === '' ? undefined : Number(event.target.value);
    apiRef.current.setEditCellValue({ id, field, value: numValue });
  };

  return (
    <TextField size="small" type="number" value={value ?? ''} onChange={handleChange} fullWidth variant="outlined" />
  );
}

export default function ManageCatalogGroupsModal({ open, onClose }: ManageCatalogGroupsModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const [modifiedRows, setModifiedRows] = useState<Record<string, PendingChanges>>({});
  const [localRows, setLocalRows] = useState<Array<CatalogGroupRowData>>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string | null>(null);

  // Fetch catalog groups
  const {
    data: catalogGroupsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accommodation', 'catalog-groups', 'all'],
    queryFn: async () => {
      const response = await AccommodationService.listCatalogGroups({ limit: '1000', page: '1' });
      return response.result;
    },
    enabled: open,
  });

  // Get unique type options for the filter dropdown
  const typeOptions = useMemo(() => {
    const apiGroups = catalogGroupsData?.groups ?? [];
    const types = new Set(apiGroups.map((group) => group.type));
    return Array.from(types).sort();
  }, [catalogGroupsData?.groups]);

  // Combine API data with local new rows (local rows first to appear at top)
  const allRows = useMemo(() => {
    const apiGroups = catalogGroupsData?.groups ?? [];
    const combinedRows = [...localRows, ...apiGroups];

    // Filter by type if filter is set
    if (filterType) {
      return combinedRows.filter((row) => row.type === filterType);
    }

    return combinedRows;
  }, [catalogGroupsData?.groups, localRows, filterType]);

  // Create catalog group mutation
  const createMutation = useMutation({
    mutationFn: async (data: AccommodationService.CatalogGroupCreatePayload & { tempId: string }) => {
      const { tempId, ...payload } = data;
      return await AccommodationService.createCatalogGroup(payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'catalog-groups'] });
      // Remove the temporary local row after successful creation
      setLocalRows((prev) => prev.filter((r) => r.id !== variables.tempId));
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.tempId];
        return updated;
      });
      enqueueSnackbar('Catalog group created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating catalog group: ${error.message}`, { variant: 'error' });
    },
  });

  // Update catalog group mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AccommodationService.CatalogGroupUpdatePayload }) => {
      return await AccommodationService.updateCatalogGroup({ id }, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'catalog-groups'] });
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.id];
        return updated;
      });
      enqueueSnackbar('Catalog group updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating catalog group: ${error.message}`, { variant: 'error' });
    },
  });

  // Delete catalog group mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await AccommodationService.deleteCatalogGroup({ id });
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'catalog-groups'] });
      setDeletingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });
      enqueueSnackbar('Catalog group deleted successfully', { variant: 'success' });
    },
    onError: (error: Error, id) => {
      setDeletingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });
      enqueueSnackbar(`Error deleting catalog group: ${error.message}`, { variant: 'error' });
    },
  });

  const handleAddNew = useCallback(() => {
    const tempId = `${NEW_ROW_ID_PREFIX}${Date.now()}`;
    const newRow: CatalogGroupRowData = {
      id: tempId,
      code: '',
      type: 'facility',
      name: '',
      description: null,
      displayOrder: undefined,
      icon: null,
      isNew: true,
    };
    // Prepend new row so it appears at the top
    setLocalRows((prev) => [newRow, ...prev]);
    setModifiedRows((prev) => ({
      ...prev,
      [tempId]: { code: '', type: 'facility', name: '' },
    }));
  }, []);

  const handleSave = useCallback(
    (row: CatalogGroupRowData) => {
      const changes = modifiedRows[row.id];
      if (!changes) return;

      if (row.isNew || row.id.startsWith(NEW_ROW_ID_PREFIX)) {
        // Create new catalog group
        if (!changes.code || !changes.type || !changes.name) {
          enqueueSnackbar('Code, Type, and Name are required for new catalog groups', { variant: 'error' });
          return;
        }
        createMutation.mutate({
          tempId: row.id,
          code: changes.code,
          type: changes.type,
          name: changes.name,
          description: changes.description,
          displayOrder: changes.displayOrder,
          icon: changes.icon,
        });
      } else {
        // Update existing catalog group
        updateMutation.mutate({
          id: row.id,
          data: {
            name: changes.name,
            description: changes.description,
            displayOrder: changes.displayOrder,
            icon: changes.icon,
          },
        });
      }
    },
    [modifiedRows, createMutation, updateMutation, enqueueSnackbar],
  );

  const handleDelete = useCallback(
    (row: CatalogGroupRowData) => {
      if (row.isNew || row.id.startsWith(NEW_ROW_ID_PREFIX)) {
        // Just remove local row
        setLocalRows((prev) => prev.filter((r) => r.id !== row.id));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[row.id];
          return updated;
        });
      } else {
        // Delete from API
        setDeletingIds((prev) => new Set(prev).add(row.id));
        deleteMutation.mutate(row.id);
      }
    },
    [deleteMutation],
  );

  const handleClearChanges = useCallback(
    (id: string) => {
      const isNewRow = id.startsWith(NEW_ROW_ID_PREFIX);

      if (isNewRow) {
        // For new rows, remove them entirely
        setLocalRows((prev) => prev.filter((r) => r.id !== id));
      } else {
        // For existing rows, restore original values
        const originalGroup = catalogGroupsData?.groups?.find((g) => g.id === id);
        if (originalGroup && apiRef.current) {
          const editableFields = ['name', 'description', 'displayOrder', 'icon'];
          editableFields.forEach((field) => {
            try {
              apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
            } catch {
              // Cell not in edit mode, ignore
            }
          });
          apiRef.current.updateRows([{ ...originalGroup }]);
        }
      }

      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    },
    [catalogGroupsData?.groups, apiRef],
  );

  const processRowUpdate = useCallback(
    (newRow: CatalogGroupRowData, _oldRow: CatalogGroupRowData) => {
      const isNewRow = newRow.id.startsWith(NEW_ROW_ID_PREFIX);
      let changedFields: PendingChanges = {};

      if (isNewRow) {
        // For new rows, track all editable fields
        changedFields = {
          code: newRow.code,
          type: newRow.type,
          name: newRow.name,
          description: newRow.description,
          displayOrder: newRow.displayOrder,
          icon: newRow.icon,
        };
      } else {
        // For existing rows, compare against original
        const originalGroup = catalogGroupsData?.groups?.find((g) => g.id === newRow.id);
        if (!originalGroup) return newRow;

        if (newRow.name !== originalGroup.name) changedFields.name = newRow.name;
        if (newRow.description !== originalGroup.description) changedFields.description = newRow.description;
        if (newRow.displayOrder !== originalGroup.displayOrder) changedFields.displayOrder = newRow.displayOrder;
        if (newRow.icon !== originalGroup.icon) changedFields.icon = newRow.icon;
      }

      if (Object.keys(changedFields).length > 0 || isNewRow) {
        setModifiedRows((prev) => ({
          ...prev,
          [newRow.id]: { ...prev[newRow.id], ...changedFields },
        }));
      } else {
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[newRow.id];
          return updated;
        });
      }

      return newRow;
    },
    [catalogGroupsData?.groups],
  );

  const columns: Array<GridColDef<CatalogGroupRowData>> = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        width: 150,
        editable: true,
        renderEditCell: (params) => {
          const isNewRow = String(params.id).startsWith(NEW_ROW_ID_PREFIX);
          if (!isNewRow) {
            return <Typography variant="body2">{params.value}</Typography>;
          }
          return <EditableTextField {...params} />;
        },
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => (
          <Typography variant="body2">{params.value ?? '-'}</Typography>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        editable: true,
        renderEditCell: (params) => {
          const isNewRow = String(params.id).startsWith(NEW_ROW_ID_PREFIX);
          if (!isNewRow) {
            return <Typography variant="body2">{params.value}</Typography>;
          }
          return <EditableTextField {...params} />;
        },
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => (
          <Typography variant="body2">{params.value ?? '-'}</Typography>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        editable: true,
        renderEditCell: (params) => <EditableTextField {...params} />,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => (
          <Typography variant="body2">{params.value ?? '-'}</Typography>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 250,
        editable: true,
        renderEditCell: (params) => <EditableTextField {...params} />,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => (
          <Typography variant="body2" noWrap title={params.value ?? ''}>
            {params.value ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: 120,
        editable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        renderEditCell: (params) => <NumberEditCell {...params} />,
      },
      {
        field: 'icon',
        headerName: 'Icon',
        width: 150,
        editable: true,
        renderEditCell: (params) => <EditableTextField {...params} />,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => (
          <Typography variant="body2" noWrap title={params.value ?? ''}>
            {params.value ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        editable: false,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const hasChanges = Boolean(modifiedRows[params.row.id]);
          const isSaving = createMutation.isPending || updateMutation.isPending;
          const isDeleting = deletingIds.has(params.row.id);
          const isNewRow = params.row.id.startsWith(NEW_ROW_ID_PREFIX);

          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Save changes">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(params.row)}
                    disabled={!hasChanges || isSaving}
                    sx={{ color: hasChanges && !isSaving ? blue[600] : undefined }}
                  >
                    {isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear changes">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearChanges(params.row.id)}
                    disabled={!hasChanges || isSaving}
                  >
                    <ClearIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Delete group'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(params.row)}
                    disabled={isDeleting || isSaving}
                    sx={{ color: red[600] }}
                  >
                    {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      modifiedRows,
      handleSave,
      handleClearChanges,
      handleDelete,
      createMutation.isPending,
      updateMutation.isPending,
      deletingIds,
    ],
  );

  const handleClose = useCallback(() => {
    // Reset local state on close
    setLocalRows([]);
    setModifiedRows({});
    setFilterType(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Manage Catalog Groups</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            Error loading catalog groups: {error instanceof Error ? error.message : 'Unknown error'}
          </Typography>
        )}

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Autocomplete
            size="small"
            options={typeOptions}
            value={filterType}
            onChange={(_event, newValue) => setFilterType(newValue)}
            renderInput={(params) => <TextField {...params} label="Filter by Type" variant="outlined" />}
            sx={{ minWidth: 200 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} size="small">
            Add New
          </Button>
        </Stack>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Click on a cell to edit. Code and Type are only editable for new rows. Click Save to persist changes.
        </Typography>

        <Box sx={{ height: 500 }}>
          <Table
            columns={columns}
            rows={allRows}
            getRowId={(row) => row.id}
            paginationMode="client"
            isLoading={isLoading}
            autoHeight={false}
            dataGridProps={{
              apiRef,
              editMode: 'cell',
              processRowUpdate,
              onProcessRowUpdateError: (error) => {
                enqueueSnackbar(`Error processing row update: ${error.message}`, { variant: 'error' });
              },
              getRowHeight: () => 'auto',
              sx: {
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
                height: '100%',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
